'use client';

import React, { useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { JobCardFormData } from './JobCardWizard';
import { AlertCircle, Search, Wrench, Camera, Upload } from 'lucide-react';

export default function Step2FaultDescription() {
  const { register, formState: { errors } } = useFormContext<JobCardFormData>();
  const [faultPhotoName, setFaultPhotoName] = useState<string>('');
  const [completionPhotoName, setCompletionPhotoName] = useState<string>('');

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3 mb-2">
        <div className="w-9 h-9 rounded-lg bg-warning/10 flex items-center justify-center">
          <Wrench size={18} className="text-warning" />
        </div>
        <div>
          <h2 className="text-lg font-semibold text-foreground">3-Stage Technical Description</h2>
          <p className="text-xs text-muted-foreground">All fields support multi-line text — no data will be truncated</p>
        </div>
      </div>

      {/* Stage 1: Fault Reported */}
      <div className="p-4 rounded-xl border border-border" style={{ backgroundColor: 'rgba(239,68,68,0.04)' }}>
        <div className="flex items-center gap-2 mb-3">
          <div className="w-6 h-6 rounded-full bg-danger/20 flex items-center justify-center">
            <AlertCircle size={13} className="text-danger" />
          </div>
          <label className="text-sm font-semibold text-foreground">
            Stage 1 — Fault Reported by Driver
          </label>
          <span className="badge badge-danger text-xs">Required</span>
        </div>
        <textarea
          {...register('faultReported', {
            required: 'Fault Reported is required — describe what the driver reported',
            minLength: { value: 20, message: 'Please provide at least 20 characters of detail' },
          })}
          className="textarea-field"
          placeholder="Describe exactly what the driver/operator reported — e.g. 'Vehicle pulling left under braking, grinding noise from front-left wheel on deceleration above 60km/h.'"
          rows={4}
        />
        {errors.faultReported && <p className="field-error">{errors.faultReported.message}</p>}
        <p className="field-hint">Record the driver's exact words where possible. Do not paraphrase technical details.</p>
      </div>

      {/* Stage 2: Faults Found */}
      <div className="p-4 rounded-xl border border-border" style={{ backgroundColor: 'rgba(245,158,11,0.04)' }}>
        <div className="flex items-center gap-2 mb-3">
          <div className="w-6 h-6 rounded-full bg-warning/20 flex items-center justify-center">
            <Search size={13} className="text-warning" />
          </div>
          <label className="text-sm font-semibold text-foreground">
            Stage 2 — Faults Found (Technician Diagnosis)
          </label>
          <span className="badge badge-warning text-xs">Required</span>
        </div>
        <textarea
          {...register('faultsFound', {
            required: 'Faults Found is required — document your technical diagnosis',
            minLength: { value: 20, message: 'Provide at least 20 characters of diagnostic detail' },
          })}
          className="textarea-field"
          placeholder="Document technical findings after inspection — e.g. 'Front-left brake pads worn to 2mm (limit 3mm). Brake disc scored with 1.5mm groove. Caliper sliding pins seized causing uneven pad wear. Brake fluid level 15% below minimum.'"
          rows={5}
        />
        {errors.faultsFound && <p className="field-error">{errors.faultsFound.message}</p>}
        <p className="field-hint">Include measurements, part numbers, and severity assessments.</p>

        {/* Fault Found Photo Upload */}
        <div className="mt-3">
          <label className="label-field flex items-center gap-1.5">
            <Camera size={13} />
            Fault Found Photos (Optional)
          </label>
          <label className="flex items-center gap-3 p-3 rounded-lg border border-dashed border-border hover:border-primary cursor-pointer transition-colors duration-150">
            <Upload size={16} className="text-muted-foreground" />
            <span className="text-sm text-muted-foreground">
              {faultPhotoName || 'Click to upload photos of the fault'}
            </span>
            <input
              type="file"
              accept="image/*"
              multiple
              className="hidden"
              onChange={(e) => {
                const files = e.target.files;
                if (files && files.length > 0) {
                  setFaultPhotoName(`${files.length} photo${files.length > 1 ? 's' : ''} selected`);
                }
              }}
            />
          </label>
          <p className="field-hint">JPG, PNG, HEIC accepted. Max 10MB per file.</p>
        </div>
      </div>

      {/* Stage 3: Repairs Done */}
      <div className="p-4 rounded-xl border border-border" style={{ backgroundColor: 'rgba(34,197,94,0.04)' }}>
        <div className="flex items-center gap-2 mb-3">
          <div className="w-6 h-6 rounded-full bg-positive/20 flex items-center justify-center">
            <Wrench size={13} className="text-positive" />
          </div>
          <label className="text-sm font-semibold text-foreground">
            Stage 3 — Repairs Done
          </label>
          <span className="badge badge-success text-xs">Required</span>
        </div>
        <textarea
          {...register('repairsDone', {
            required: 'Repairs Done is required — document all work performed',
            minLength: { value: 20, message: 'Provide at least 20 characters describing the repairs' },
          })}
          className="textarea-field"
          placeholder="List all repairs performed — e.g. 'Replaced front-left and front-right brake pad sets (Part BRK-2291). Machined front brake discs to within spec. Freed and greased caliper sliding pins. Flushed and topped brake fluid to MAX. Road tested — braking straight and noise-free.'"
          rows={5}
        />
        {errors.repairsDone && <p className="field-error">{errors.repairsDone.message}</p>}
        <p className="field-hint">List every repair action taken, parts replaced, and fluids topped up.</p>

        {/* After Completion Photo Upload */}
        <div className="mt-3">
          <label className="label-field flex items-center gap-1.5">
            <Camera size={13} />
            After Completion Photos (Optional)
          </label>
          <label className="flex items-center gap-3 p-3 rounded-lg border border-dashed border-border hover:border-primary cursor-pointer transition-colors duration-150">
            <Upload size={16} className="text-muted-foreground" />
            <span className="text-sm text-muted-foreground">
              {completionPhotoName || 'Click to upload after-repair photos'}
            </span>
            <input
              type="file"
              accept="image/*"
              multiple
              className="hidden"
              onChange={(e) => {
                const files = e.target.files;
                if (files && files.length > 0) {
                  setCompletionPhotoName(`${files.length} photo${files.length > 1 ? 's' : ''} selected`);
                }
              }}
            />
          </label>
          <p className="field-hint">Document the completed repair state. These appear in the PDF report.</p>
        </div>
      </div>
    </div>
  );
}